
import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import OrderManager from './components/OrderManager';
import VarietyManager from './components/VarietyManager';
import ProductionCalendar from './components/ProductionCalendar';
import InventoryHistory from './components/InventoryHistory';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import CustomerManager from './components/CustomerManager';
import SowingManager from './components/SowingManager';
import DeliveryManager from './components/DeliveryManager';
import RecurringRules from './components/RecurringRules';
import './App.css';
import SafeIcon from './common/SafeIcon';
import { FiMenu, FiLayers, FiTruck, FiShoppingCart, FiCalendar } from 'react-icons/fi';

// Protected Route Wrapper
const ProtectedRoute = ({ children, isAuthenticated }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const navItems = [
    { path: '/sowing', icon: FiLayers, label: 'Sowing' },
    { path: '/delivery', icon: FiTruck, label: 'Delivery' },
    { path: '/orders', icon: FiShoppingCart, label: 'Orders' },
    { path: '/calendar', icon: FiCalendar, label: 'Calendar' },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 w-full md:ml-64 transition-all duration-300 pb-24 md:pb-0">
        {/* Mobile Header */}
        <div className="md:hidden bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10 shadow-sm">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 -ml-2 text-gray-500 hover:text-[#1e3a8a] rounded-lg active:bg-gray-50 transition-colors"
              aria-label="Toggle Menu"
            >
              <SafeIcon icon={FiMenu} className="text-2xl" />
            </button>
            <div className="w-8 h-8 rounded-full overflow-hidden border border-green-50 shadow-sm">
              <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-lg font-light text-[#1e3a8a] tracking-wide">LETSGREEN</h1>
          </div>
        </div>

        {children}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-40 px-6 py-2 flex justify-between items-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center space-y-0.5 ${isActive ? 'text-[#1e3a8a]' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <SafeIcon icon={item.icon} className={`text-xl mb-0.5 ${isActive ? 'stroke-2' : ''}`} />
              <span className={`text-[9px] uppercase tracking-wide ${isActive ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [orders, setOrders] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [batches, setBatches] = useState([]);
  const [customers, setCustomers] = useState([]);

  const fetchData = async () => {
    try {
      const [varietiesRes, ordersRes, batchesRes, customersRes] = await Promise.all([
        fetch('/api/varieties'),
        fetch('/api/orders'),
        fetch('/api/batches'),
        fetch('/api/customers')
      ]);

      if (varietiesRes.ok) {
        const varietiesData = await varietiesRes.json();
        setVarieties(varietiesData);
      }

      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData);
      }

      if (batchesRes.ok) {
        const batchesData = await batchesRes.json();
        setBatches(batchesData);
      }

      if (customersRes.ok) {
        const customersData = await customersRes.json();
        setCustomers(customersData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('microgreens-auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
    setIsAuthChecking(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('microgreens-batches', JSON.stringify(batches));
  }, [batches]);

  const handleLogin = (status) => {
    setIsAuthenticated(status);
    localStorage.setItem('microgreens-auth', status);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('microgreens-auth');
  };

  if (isAuthChecking) {
    return <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#16a34a]"></div>
    </div>;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />

        <Route path="/" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <Dashboard orders={orders} varieties={varieties} batches={batches} onUpdate={fetchData} />
          </ProtectedRoute>
        } />

        <Route path="/orders" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <OrderManager orders={orders} onUpdate={fetchData} varieties={varieties} batches={batches} setBatches={setBatches} customers={customers} />
          </ProtectedRoute>
        } />

        <Route path="/varieties" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <VarietyManager varieties={varieties} onUpdate={fetchData} />
          </ProtectedRoute>
        } />

        <Route path="/customers" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <CustomerManager onUpdate={fetchData} />
          </ProtectedRoute>
        } />

        <Route path="/calendar" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <ProductionCalendar orders={orders} varieties={varieties} batches={batches} />
          </ProtectedRoute>
        } />

        <Route path="/history" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <InventoryHistory orders={orders} varieties={varieties} />
          </ProtectedRoute>
        } />

        <Route path="/sowing" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <SowingManager orders={orders} varieties={varieties} batches={batches} onUpdate={fetchData} />
          </ProtectedRoute>
        } />

        <Route path="/delivery" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <DeliveryManager orders={orders} varieties={varieties} batches={batches} customers={customers} onUpdate={fetchData} />
          </ProtectedRoute>
        } />

        <Route path="/recurring-rules" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <RecurringRules />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;